import { Roles } from "../../constants/roles";
import { FormBase } from "../../util/form-base-form";

export type RankingMonthlyTabType = "product" | "user";

export class RankingsMonthlyTabsForm extends FormBase {
    public root: HTMLElement;

    public constructor(root: HTMLElement) {
        super();
        this.root = root;
    }

    public static getRoots(root: ParentNode): HTMLElement[] {
        return new RankingsMonthlyTabsForm(document.body).queryElements(
            root,
            `[data-role="${Roles.RANKINGS_MONTHLY_TABS}"]`,
            HTMLElement,
        );
    }

    public getRoot(): HTMLElement {
        return this.root;
    }

    public isInitialized(): boolean {
        return this.root.dataset.rankingsMonthlyTabsInit === "1";
    }

    public markInitialized(): void {
        this.root.dataset.rankingsMonthlyTabsInit = "1";
    }

    public getTabs(): HTMLElement[] {
        return this.queryElements(
            this.root,
            `[data-role="${Roles.RANKINGS_MONTHLY_TAB}"][data-ranking-type]`,
            HTMLElement,
        );
    }

    public getPanels(): HTMLElement[] {
        return this.queryElements(
            this.root,
            `[data-role="${Roles.RANKINGS_MONTHLY_PANEL}"][data-ranking-type]`,
            HTMLElement,
        );
    }

    public resolveTypeFromHash(): RankingMonthlyTabType | null {
        const raw = typeof location !== "undefined" ? location.hash : "";
        const id = typeof raw === "string" ? raw.replace(/^#/, "") : "";
        if (!id) return null;

        const escaped = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(id) : id;
        const el = this.queryElement(this.root, `#${escaped}`, HTMLElement);
        if (!(el instanceof HTMLElement)) return null;

        const type = el.dataset.rankingType;
        return type === "product" || type === "user" ? type : null;
    }

    public getInitialType(): RankingMonthlyTabType | null {
        const type = this.root.dataset.initialType;
        return type === "product" || type === "user" ? type : null;
    }

    public getMonthPickerScope(): Element {
        return this.closest(this.root, ".rankings-monthly-tabs", HTMLElement) ?? this.root;
    }

    public getMonthInput(): HTMLInputElement | null {
        return this.queryElement(
            this.getMonthPickerScope(),
            `[data-role="${Roles.RANKINGS_MONTHLY_MONTH_PICKER}"]`,
            HTMLInputElement,
        );
    }

    public getMonthPrevButton(): HTMLButtonElement | null {
        return this.queryElement(
            this.getMonthPickerScope(),
            `[data-role="${Roles.RANKINGS_MONTHLY_MONTH_PREV}"]`,
            HTMLButtonElement,
        );
    }

    public getMonthNextButton(): HTMLButtonElement | null {
        return this.queryElement(
            this.getMonthPickerScope(),
            `[data-role="${Roles.RANKINGS_MONTHLY_MONTH_NEXT}"]`,
            HTMLButtonElement,
        );
    }

    public getParentTabsRoot(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${Roles.RANKINGS_MONTHLY_PARENT_TABS}"]`, HTMLElement);
    }

    public getParentTabs(): HTMLElement[] {
        const parentRoot = this.getParentTabsRoot() ?? this.root;
        return this.queryElements(
            parentRoot,
            `[data-role="${Roles.RANKINGS_MONTHLY_PARENT_TAB}"][data-parent-category-id]`,
            HTMLElement,
        );
    }

    public getParentPanels(): HTMLElement[] {
        return this.queryElements(
            this.root,
            `[data-role="${Roles.RANKINGS_MONTHLY_PARENT_PANEL}"][data-parent-category-id]`,
            HTMLElement,
        );
    }

    public getInitialParentCategoryId(): string | null {
        const value = (this.root.dataset.initialParentCategoryId || "").trim();
        return value !== "" ? value : null;
    }
}
